import type {
  AttdefEntity,
  DrawingDocument,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { classifyEntityChange } from "./entityChangedClassify";
import {
  emitEntityChanged,
  type EntityChangedEvent,
} from "./entityChangedBus";
import {
  EntityMetadataIndex,
  type EntityMetadata,
  type EntityMetadataAggregates,
} from "./entityMetadataIndex";
import {
  appendEntity as appendCompactEntity,
  compactCompactStore,
  createCompactStore,
  entityFromCompactStore,
  tombstoneFraction,
  type CompactStore,
  type CompactStoreKind,
} from "./compactStores/aggregateStore";
import {
  markTombstone,
  type BaseColumns,
} from "./compactStores/compactStoreBase";
import { markLineTombstone } from "./compactEntityStore";
import {
  createDocumentMemoryHolder,
  forEachLiveEntity,
  forEachLiveEntityInSpace,
  iterateLiveEntitiesInSpace,
  snapshotSpaceEntitiesFromHolder,
  type CompactEntityLocation,
  type DocumentMemoryHolder,
} from "./documentMemoryHolder";
import {
  dependencyBatchFromMutation,
  rebuildHolderDependencyIndex,
  refreshBlockDefDependencyContainers,
  type DependencyIndex,
} from "./dependencyIndex";
import { NO_MUTATIONS, type EntityMutation } from "./types";
import {
  recordAppend,
  recordAppendBatch,
  recordCacheVersionBump,
  recordMetadataIndexVersionBump,
  recordSnapshot,
} from "./importMetrics";

const perfNow = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

/**
 * Track 1 / Phase 1 — non-reactive owners of the lightweight metadata
 * index, the typed-array compact entity store, and the reactive version
 * counter that ties them to React subscribers.
 *
 * Why module-scope (not Zustand state):
 *   - 685k entities cannot live inside a Zustand reactive slice without
 *     serialising every state-update through the React reconciler.
 *   - These structures are mutated in-place; reactivity for consumers
 *     is signalled via `entityMetadataIndexVersion` (Zustand state),
 *     bumped here on every commit.
 *
 * Why a separate version counter from the Zustand `entityMetadataIndexVersion`:
 *   - Source of truth for "did the index change?" is this module.
 *   - editorStore mirrors the value into Zustand for React subscription;
 *     `commitMutation` calls `bumpMetadataIndexVersion()` and forwards
 *     the new value into `set({ entityMetadataIndexVersion: ... })`.
 *
 * Track-1 plan: docs/plans/2026-05-06_state_layer_unification.md (v4),
 * Phase 1 §"API / типы".
 *
 * post-V9 Section 0 Iter 6.D — the legacy
 * `entitiesCache: Map<spaceId, DrawingEntity[]>` was removed in this
 * iteration. Per-space entity arrays are owned by the reactive
 * `geometryDocument.spaces[].entities` (Phase 3b PR #6
 * slim-state-as-pointer keeps that array populated for huge imports
 * too); per-entity O(1) lookups go through the compact cache id-index.
 * The architectural cleanup eliminates the redundant Map alias.
 *
 * Memory: ~5GB peak on 685k-entity imports because the entity payload
 * itself is the dominant cost; the next memory step (Iter 7) is to
 * slim `space.entities` and read renderer + reducers exclusively from
 * the compact cache.
 *
 * Phase 4 Wave 1 (multi-document) — the module-level singletons are
 * replaced by a `Map<documentId, DocumentMemoryHolder>` keyed by the
 * caller-supplied id. A single "active" doc id selects which holder
 * the existing exports route through; if `setActiveDocument` was never
 * called, the module falls through to a `__default__` doc, so all
 * pre-Phase-4 callers continue to work unchanged. Two documents
 * coexisting in memory (e.g. compare-views, future paste-from-clipboard
 * with foreign drawings) no longer collide.
 *
 * Public surface additions (Phase 4 Wave 1):
 *   - `setActiveDocument(docId)` — switch routing target.
 *   - `getActiveDocumentId()` — diagnostic.
 *   - `clearDocument(docId)` — drop a holder AND its provenance ledger
 *     together, atomically. The general-purpose "close this document for
 *     good" primitive — used today by this file's own unit tests
 *     (`entityProvenance.test.ts`, `multiDocument.test.ts`) exercising
 *     that combined contract, and the intended call for a future
 *     synchronous "close this document tab" UI action. NOT used by the
 *     OPEN (replace) import path — see `dropDocumentHolder` /
 *     `dropDocumentProvenance` below, and the `EntityProvenance`
 *     ownership-rule doc comment, for why that path needs the two halves
 *     split across an async boundary instead.
 *   - `clearEntityProvenance(docId)` — drop ONLY a document's provenance
 *     ledger; used by the REPLACEMENT-import seam (same-id OPEN into an
 *     empty seed).
 *   - `dropDocumentHolder(docId)` / `dropDocumentProvenance(docId)` — the
 *     OPEN (replace) import path's split halves of `clearDocument`'s
 *     contract, called at two different points in the same async action
 *     so a failed/aborted parse never tears down a still-visible
 *     document's ledger.
 *   - `listOpenDocuments()` — diagnostic / tests.
 *   - `snapshotSpaceEntities(docId, spaceId)` — drain the live (non-
 *     tombstoned) entities of a given space; used by the writer + the
 *     DrawingSpace read-through wrapper (Task B/C).
 *   - `DEFAULT_DOCUMENT_ID` — exported sentinel for callers that need
 *     to reference the fall-through holder explicitly.
 */

/** Phase 4 Wave 1 — fall-through doc id when no caller called setActiveDocument. */
export const DEFAULT_DOCUMENT_ID = "__default__";

// Phase 4 Wave 1 — `CompactEntityLocation` + `DocumentMemoryHolder`
// shapes and the `snapshotSpaceEntitiesFromHolder` reader live in
// `./documentMemoryHolder` so the editorStore dispatch boundary
// (Task B read-through wrapper) can build wrappers without importing
// the full module-level API.
export type {
  CompactEntityLocation,
  DocumentMemoryHolder,
} from "./documentMemoryHolder";

/** Phase 4 Wave 1 — registry of open documents. */
const documentHolders = new Map<string, DocumentMemoryHolder>();

/** Phase 4 Wave 1 — currently active doc id. */
let activeDocumentId: string = DEFAULT_DOCUMENT_ID;

/**
 * Entity-provenance ledger — session-local, per document id, PARALLEL to
 * `documentHolders` (not a `DocumentMemoryHolder` field: this is a
 * cosmetic audit label, not compact-store/streaming state, so it does
 * not need the columnar-store machinery `DocumentMemoryHolder` exists
 * for). `clearDocument` drops both maps together (below) so a closed
 * document never leaves an orphaned ledger behind, matching this file's
 * "no orphaned half-state" rule for anything keyed by doc id.
 *
 * This is an "audit record" in the ПРИОРИТЕТ #2.1 sense — a DERIVED
 * representation, the same bucket `entityMetadataIndex` lives in — not a
 * second CAD-model owner. It answers exactly one question for the
 * Properties-panel preview builder (`dxfGeometry.ts`): did this entity
 * id ever pass through `commitMutation`'s `added` array (i.e. was it
 * created/restored by a command, agent, or collaboration edit), or was
 * it never registered here at all (i.e. it arrived through the
 * import/bootstrap pipeline, which — like every bulk-import call in this
 * module, `populateCompactCache*` — writes the compact cache directly
 * and deliberately never calls `commitMutation`; ПРИОРИТЕТ #2.1's
 * "specialized import/bootstrap pipeline" exception). It is never
 * consulted for geometry, export, undo/redo replay, or any other
 * CAD-model decision.
 *
 * Not the same thing as `DrawingDocument.provenance` (model.ts,
 * DXF-CONFORMANT-SKELETON) — that is a WHOLE-DOCUMENT marker set once at
 * import time and used to decide from-scratch vs. imported for skeleton
 * back-fill; it never changes as entities are added/removed afterward, so
 * it cannot answer "was THIS entity added after the document was already
 * open" — exactly the per-entity question this ledger exists for.
 *
 * OWNERSHIP RULE (GitHub Codex review PR #582) — a ledger belongs to the
 * DOCUMENT it describes, and dies with it — but "dies with it" splits
 * into two different teardown SHAPES depending on whether the seam has an
 * async failure window between "old document torn down" and "new document
 * confirmed":
 *   - `clearDocument(docId)` / `clearMutationStore()` — explicit,
 *     synchronous, COMBINED teardown of a holder (below). Used wherever
 *     there is no such window: this file's own unit tests exercising the
 *     combined contract directly, and any future synchronous "close this
 *     document" caller.
 *   - a fresh OPEN that mints a NEW document id
 *     (`importDxfFileAsyncReplacing` in `editorStoreImportFileActions.ts`)
 *     — NOT `clearDocument`. There IS an async failure window here (the
 *     new file's parse can fail or be aborted), so the teardown is SPLIT:
 *     `dropDocumentHolder(previousDocId)` runs EAGERLY, before the new
 *     file has even started parsing, to free the outgoing document's
 *     (potentially multi-GB) compact-cache holder — memory headroom for
 *     the incoming import, so the process never holds two huge documents
 *     at once. `dropDocumentProvenance(previousDocId)` — the ledger half
 *     — is deliberately DEFERRED until the swap's `set(...)` call has
 *     actually returned, the earliest point `geometryDocument` genuinely
 *     IS the new document. If the parse fails or aborts before that
 *     point, `previousDocId`'s ledger was never touched, and the shared
 *     `catch` restores `activeDocumentId` to `previousDocId` — so the
 *     still-visible outgoing document's provenance reads stay correct
 *     throughout (and after) an abandoned OPEN attempt. Three rounds of
 *     increasingly precise GitHub Codex findings on this exact ordering
 *     (teardown-before-swap, routing-restore-after-a-later-failure,
 *     ledger-drop-before-the-swap's-own-`set()`-returns) are what
 *     produced this split — see the PR history for `dropDocumentHolder`
 *     if the reasoning needs re-deriving.
 *   - a REPLACEMENT import that REUSES the current document id (the
 *     "empty seed" OPEN bypass in `editorStoreImportActions.ts` /
 *     `editorStoreImportFileActions.ts`: `{...imported, id: current.id}`)
 *     — those call sites call `clearEntityProvenance(docId)` explicitly
 *     (below), synchronously, once the merge is known to apply — no async
 *     failure window on this path (the merge itself does not swap
 *     documents, so there is nothing to await between the clear and the
 *     write).
 * `clearCompactCache()` itself is deliberately NOT a clearing hook: it
 * runs for BOTH a replacement OPEN and an INSERT/merge into a LIVE
 * document, and clearing on the latter would wipe "authored" tracking for
 * every SURVIVING host entity. The empty-seed flag the import paths
 * already compute (`isDocumentEffectivelyEmpty`) is the authoritative
 * replace-vs-merge signal, so the clear is hooked THERE — where the
 * distinction is actually known — not here.
 */
export type EntityProvenance = "imported" | "authored";

/**
 * Ledger shape: `id → classification of the entity that most recently
 * held that id`. One interned string literal per tracked id — no
 * per-entry object, no content fingerprint, nothing that scales with
 * entity complexity (a HATCH with thousands of loop points costs exactly
 * what a POINT costs).
 *
 * A `removed` id KEEPS its entry (a tombstone) so a later history restore
 * can inherit the real classification; a forward create that recycles the
 * id OVERWRITES it (see `recordEntityProvenance`).
 */
const entityProvenanceByDocument = new Map<
  string,
  Map<string, EntityProvenance>
>();

function provenanceLedger(docId: string): Map<string, EntityProvenance> {
  let ledger = entityProvenanceByDocument.get(docId);
  if (ledger === undefined) {
    ledger = new Map();
    entityProvenanceByDocument.set(docId, ledger);
  }
  return ledger;
}

/**
 * Provenance of `entityId` in `documentId` (defaults to the ACTIVE
 * document when omitted — the same "legacy single-document call site"
 * convention every other un-parameterised export in this module follows,
 * e.g. `getMetadataIndexVersion`, `getCompactCache`). Absent from the
 * ledger defaults to `"imported"` — correct for every entity that reached
 * the document through the import/bootstrap pipeline (see the ledger doc
 * comment above) and for any call site (tests included) that builds a
 * preview without ever routing through `commitMutation`.
 *
 * local-Codex-review P2 — pass the entity's OWNING document id explicitly
 * whenever it is known (dense per-type ids like "line-1" are not globally
 * unique across open documents) rather than relying on "whichever document
 * happens to be active right now", which can diverge from the document
 * actually being read (e.g. a caller resyncing a background/inactive
 * document's previews).
 */
export const getEntityProvenance = (
  entityId: string,
  documentId?: string,
): EntityProvenance =>
  // local-Codex-review round-4 P3 — read via the NON-allocating `.get`
  // (not the `provenanceLedger()` allocate-on-miss helper, which is for
  // WRITE sites only). Every preview build calls this; using the
  // allocating helper here would mint and retain an empty per-document
  // `Map` for every document merely PREVIEWED (e.g. a background/inactive
  // document resync), never freed until an explicit teardown
  // (`clearDocument`, `dropDocumentProvenance`, or `clearEntityProvenance`).
  entityProvenanceByDocument.get(documentId ?? activeDocumentId)?.get(entityId) ??
  "imported";

/**
 * GitHub Codex review PR #582 finding 2 — drop `docId`'s provenance
 * ledger. Called by the import paths at the ONE point they know the
 * incoming document REPLACES the current one while REUSING its id (the
 * `isDocumentEffectivelyEmpty` "empty seed OPEN" bypass); every other
 * document-death seam is already covered by `clearDocument` /
 * `clearMutationStore` (synchronous, no swap) or the
 * `dropDocumentHolder` + `dropDocumentProvenance` pair (the OPEN path's
 * async, swap-gated close). See the ownership rule on `EntityProvenance`.
 *
 * Defaults to the ACTIVE document when omitted, matching every other
 * un-parameterised export in this module.
 */
export const clearEntityProvenance = (documentId?: string): void => {
  entityProvenanceByDocument.delete(documentId ?? activeDocumentId);
};

/**
 * Context `commitMutation` forwards into {@link recordEntityProvenance}.
 */
export interface EntityProvenanceCommitContext {
  /**
   * TRUE when this commit is the HISTORY machinery re-applying a stored
   * command rather than the user/agent issuing a new one — i.e. the
   * dispatch carried `__bypassHistory: true` (`history/undo`,
   * `history/redo`, `history/commit-batch`'s rollback,
   * `history/replay-forwards`, `history/replay-inverses`; see
   * `editorStore.ts`'s `isBypassHistory`).
   *
   * This is the AUTHORITATIVE restore-vs-fresh-authorship signal. It
   * replaced a content-hash comparison (GitHub Codex review PR #582
   * finding 1): geometric equality is not identity, so a brand-new entity
   * drawn with the same geometry as an erased one — under the same
   * recycled dense id — used to be classified as an undo restore and
   * inherit the erased entity's provenance.
   */
  historyReplay?: boolean;
}

/**
 * Called once per `commitMutation` with the SAME `mutations` the reducer
 * declared — the single choke point that can tell a genuinely NEW entity
 * (created by a command/agent/collab edit) apart from a document that
 * merely mutated pre-existing entities.
 *
 * `removed` runs FIRST and SEEDS a tombstone for every about-to-be-removed
 * id that has no entry yet (an id that never passed through here was, by
 * this ledger's default, "imported"). The entry is deliberately RETAINED
 * after the removal so a later history restore can inherit the real
 * classification, and so `getEntityProvenance` still answers truthfully
 * for the entity that last held the id.
 *
 * `added` is then classified by ONE question — was this add issued by the
 * history machinery (`context.historyReplay`) or by a forward command?
 *   - HISTORY REPLAY + the ledger already holds an entry for the id → the
 *     entity being put back is the one that entry describes → INHERIT it
 *     (undo of an ERASE keeps "imported"; redo of a CREATE keeps
 *     "authored").
 *   - anything else — a FORWARD create, or a replay with no prior entry
 *     (redo of a create whose ledger was reset) → "authored". A forward
 *     `added` is a genuine authorship EVEN IF the id was recycled from an
 *     erased entity AND the new geometry is byte-identical to it (GitHub
 *     Codex review PR #582 finding 1: `nextDenseEntityIdWithBlockSeed`
 *     re-mints the exact same "line-1" after the only LINE is erased, and
 *     the user is free to redraw the same line by hand).
 *
 * Cost — GitHub Codex review PR #582 finding 3: this function is O(added +
 * removed + updated) and NEVER reads `space.entities`. The previous
 * content-hash implementation had to resolve every removed entity's
 * CONTENT first, which walked `prevDoc.spaces[].entities` — a getter that
 * MATERIALISES the whole space on a cache-backed/read-through document —
 * making a single-entity erase O(N) in the document size (plus N entity
 * allocations) on exactly the streamed 685k-entity imports the compact
 * store exists to keep off the heap.
 *
 * `updated` only acts on an id-changing mutation (e.g. a type-conversion
 * that mints a new id) and carries the previous id's classification
 * forward (a rename is not a removal) so the converted entity keeps its
 * real provenance instead of silently defaulting to "imported". A
 * compound edit that mints an ENTIRELY NEW id via `removed` + `added`
 * instead of `updated` (e.g. TRIM's Circle→Arc type-conversion, BREAK's
 * surviving fragments) is NOT covered by this carry-forward — the new id
 * has no prior ledger entry, so it reads "authored". This is treated as
 * the correct AutoCAD-parity answer, not a gap: those operations mint a
 * genuinely NEW entity (new handle), so "authored via a command" is the
 * accurate provenance even when the source geometry traces back to an
 * imported entity.
 *
 * Known residual limitation (documented, not silently assumed away): the
 * ledger is keyed by ENTITY ID, and dense ids are recycled, so it holds
 * ONE classification per id. If an id is removed TWICE with a different
 * entity behind each removal (erase an imported LINE → draw a new one
 * that recycles "line-1" → undo the draw → undo the erase), the final
 * restore inherits the most recent tombstone rather than the one the
 * reversed history entry actually removed. Resolving that needs a stable
 * per-ENTITY identity that survives removal — the ledger cannot invent
 * one, and content hashing (the previous attempt) provably cannot stand
 * in for it. The pre-existing hash implementation was wrong in this same
 * scenario, so nothing regresses here.
 */
export const recordEntityProvenance = (
  mutations: EntityMutation,
  context?: EntityProvenanceCommitContext,
): void => {
  if (
    mutations.added.length === 0 &&
    mutations.removed.length === 0 &&
    mutations.updated.length === 0
  ) {
    return;
  }
  const ledger = provenanceLedger(activeDocumentId);
  for (const m of mutations.removed) {
    if (!ledger.has(m.entityId)) ledger.set(m.entityId, "imported");
  }
  for (const m of mutations.updated) {
    if (m.prev.id !== m.next.id) {
      const prior = ledger.get(m.prev.id);
      ledger.delete(m.prev.id);
      // local-Codex-review round-4 P2 — UNCONDITIONALLY overwrite
      // `m.next.id`'s entry (never gate on `!ledger.has(m.next.id)`). Live
      // entity ids are unique, so any entry already sitting under
      // `m.next.id` can only be a STALE tombstone from a DIFFERENT,
      // now-gone entity that previously held that id (e.g. that id was
      // erased earlier and never restored) — never a legitimate
      // classification for the entity being renamed INTO it. Gating on
      // "already has an entry" let that stale tombstone win, silently
      // keeping an authored entity's converted id "imported" (or vice
      // versa). `prior ?? "imported"` covers `m.prev.id` having no entry
      // of its own (a virgin, never-authored id being renamed) the same
      // way the rest of this ledger defaults absence.
      ledger.set(m.next.id, prior ?? "imported");
    }
  }
  const historyReplay = context?.historyReplay === true;
  for (const m of mutations.added) {
    if (historyReplay && ledger.has(m.entity.id)) continue;
    ledger.set(m.entity.id, "authored");
  }
};

function getOrCreateHolder(docId: string): DocumentMemoryHolder {
  let holder = documentHolders.get(docId);
  if (holder === undefined) {
    holder = createDocumentMemoryHolder();
    documentHolders.set(docId, holder);
  }
  return holder;
}

/** Phase 4 Wave 1 — internal accessor for the active holder. */
function activeHolder(): DocumentMemoryHolder {
  return getOrCreateHolder(activeDocumentId);
}

/**
 * Phase 4 Wave 1 — switch which document the module-level exports route
 * through. Must be called before reducers / cache writes that should
 * target the given document. Creates the holder if it does not yet
 * exist (so callers can call `setActiveDocument` then `populateCompactCache`
 * for a brand-new drawing).
 *
 * Side-effect free for the previous active doc — its holder remains in
 * the registry, and re-activating it later via `setActiveDocument`
 * restores the prior state.
 */
export const setActiveDocument = (docId: string): void => {
  activeDocumentId = docId;
  getOrCreateHolder(docId);
};

/** Phase 4 Wave 1 — current active doc id (diagnostic). */
export const getActiveDocumentId = (): string => activeDocumentId;

/**
 * Phase 4 Wave 1 — drop the holder for a doc id. Future calls that
 * resolve through this id (active or named) will lazily create a fresh
 * empty holder.
 *
 * If `docId` is the active doc, the active pointer is left in place;
 * the next operation will recreate the empty holder under the same id.
 * Callers that want to fully reset to the default doc should also call
 * `setActiveDocument(DEFAULT_DOCUMENT_ID)` afterwards.
 */
export const clearDocument = (docId: string): void => {
  documentHolders.delete(docId);
  // Keep the provenance ledger (above) in lockstep — no orphaned
  // half-state for a closed document.
  entityProvenanceByDocument.delete(docId);
};

/**
 * PR #582 live Codex finding ("Delay provenance teardown until OPEN
 * commits") — the OPEN (replace) import path must free the OUTGOING
 * document's (potentially multi-GB) compact-cache holder BEFORE the new
 * file has finished parsing, so the incoming import has memory headroom
 * instead of holding two huge documents at once. But `clearDocument`
 * drops the holder AND the provenance ledger together, and OPEN's parse
 * can still fail/abort AFTER that early call — the outer `catch` only
 * resets `importProgress` and returns `ok:false`; the Zustand
 * `geometryDocument` is never swapped, so the OUTGOING document stays
 * the one visible in the UI. A combined `clearDocument` call there wipes
 * that still-visible document's provenance ledger too, so its
 * command-authored entities start reading as "imported" in the
 * Properties panel — the same "provenance outliving/not-outliving the
 * document it describes" class as the other three findings this PR
 * already fixed.
 *
 * `dropDocumentHolder` drops ONLY the (memory-heavy) compact-cache
 * holder, deliberately leaving the (cheap, small) provenance ledger
 * intact. Pair with `dropDocumentProvenance` — called once the import is
 * KNOWN to have succeeded — to reproduce `clearDocument`'s combined
 * "no orphaned half-state" contract without tearing down the ledger for
 * a document that might still be on screen.
 */
export const dropDocumentHolder = (docId: string): void => {
  documentHolders.delete(docId);
};

/**
 * PR #582 live Codex finding — counterpart of `dropDocumentHolder`. Drop
 * ONLY the provenance ledger for `docId`, called once a document is
 * confirmed replaced (the new document's parse succeeded AND the store
 * swap landed) so the outgoing document's ledger is torn down at the
 * same moment the outgoing document itself stops being reachable —
 * never before.
 */
export const dropDocumentProvenance = (docId: string): void => {
  entityProvenanceByDocument.delete(docId);
};

/** Phase 4 Wave 1 — debug / test helper. */
export const listOpenDocuments = (): string[] => {
  return Array.from(documentHolders.keys());
};

/**
 * Phase 4 Wave 1 codex P2 — read-only peek at a holder by id WITHOUT
 * creating one on miss. Distinct from `getOrCreateHolder`: callers that
 * only need to know "does this doc have any cache state?" must not
 * pollute the registry. Used by `wrapSpaceAsReadThrough` to gate its
 * fallback on the actual presence of cache entries (idIndex size) rather
 * than on holder existence — `clearMutationStore()` and other paths can
 * eagerly mint an empty holder, which would otherwise mask the legacy
 * inline-`space.entities` fallback.
 */
export const peekDocumentMemoryHolder = (
  docId: string,
): DocumentMemoryHolder | undefined => documentHolders.get(docId);

/**
 * post-V9 Section 0 Iter 5 — discriminate `entity.type` to a
 * `CompactStoreKind` matching the per-type store an `appendEntity` call
 * would write into. Returns `undefined` for unsupported / "Other"
 * primitives so the id-index population path can skip them.
 */
function compactKindForEntity(
  entity: DrawingEntity | AttdefEntity,
): CompactEntityLocation["storeKind"] | undefined {
  switch (entity.type) {
    case "Line":
      return "lines";
    case "Point":
      return "points";
    case "Circle":
      return "circles";
    case "Arc":
      return "arcs";
    case "Ellipse":
      return "ellipses";
    case "Spline":
      return "splines";
    case "LWPOLYLINE":
      return "lwpolylines";
    case "Text":
      return "texts";
    case "MText":
      return "mtexts";
    case "Dimension":
      return "dimensions";
    case "Multileader":
      return "multileaders";
    case "Hatch":
      return "hatches";
    case "Block":
      return "blockInserts";
    case "Rectangular Associative Array":
      return "arrayRects";
    case "Viewport":
      return "viewports";
    case "Revision Cloud":
      return "revisionClouds";
    case "Wipeout":
      return "wipeouts";
    case "Attdef":
      return "attdefs";
    case "Polyline":
      return "polylines";
    case "Table":
      return "tables";
    case "OLE Frame":
      return "oleFrames";
    default:
      return undefined;
  }
}

/**
 * Phase 4 Wave 1 — public facade that proxies the active doc's
 * metadata index. Replaces the legacy module-scope `EntityMetadataIndex`
 * singleton.
 *
 * All method calls delegate to the active holder via `activeHolder()`.
 * Switching docs (`setActiveDocument`) transparently retargets every
 * future call without changing the imported reference, so the dozens
 * of existing `entityMetadataIndex.method(...)` call sites continue
 * to work unchanged.
 *
 * Why a class extending `EntityMetadataIndex` rather than a Proxy on
 * the underlying instance:
 *   - The backing class uses private fields; Proxy delegation can't
 *     reach them through the forwarding handler.
 *   - Forwarding is one line per method — cheap and explicit.
 *   - `size` is a getter on the original class; we mirror it as a
 *     getter on the facade so `entityMetadataIndex.size` keeps working.
 *   - Extending the base class makes the facade nominally assignable
 *     to consumers typed as `EntityMetadataIndex` (e.g.
 *     `getEntityMetadataIndex(): EntityMetadataIndex` in editorStore,
 *     `buildSpatialRecordsFromMetadata(metadataIndex: EntityMetadataIndex, …)`
 *     in spatialBuilder). The base class's private fields exist on
 *     the facade instance but are never touched — every public method
 *     is overridden to delegate to `activeHolder().metadataIndex`.
 */
class EntityMetadataIndexFacade extends EntityMetadataIndex {
  override get size(): number {
    return activeHolder().metadataIndex.size;
  }

  override get(id: string): EntityMetadata | undefined {
    return activeHolder().metadataIndex.get(id);
  }

  override set(metadata: EntityMetadata): void {
    activeHolder().metadataIndex.set(metadata);
  }

  override delete(id: string): boolean {
    return activeHolder().metadataIndex.delete(id);
  }

  override applyBatch(input: {
    upserts: ReadonlyArray<EntityMetadata>;
    deletes: ReadonlyArray<string>;
  }): void {
    activeHolder().metadataIndex.applyBatch(input);
  }

  override bulkLoad(records: ReadonlyArray<EntityMetadata>): void {
    activeHolder().metadataIndex.bulkLoad(records);
  }

  override *values(): IterableIterator<EntityMetadata> {
    yield* activeHolder().metadataIndex.values();
  }

  override aggregates(): EntityMetadataAggregates {
    return activeHolder().metadataIndex.aggregates();
  }

  override filterBySpace(spaceId: string): EntityMetadata[] {
    return activeHolder().metadataIndex.filterBySpace(spaceId);
  }

  override filterByType(type: string): EntityMetadata[] {
    return activeHolder().metadataIndex.filterByType(type);
  }

  override markBoundsStale(id: string): void {
    activeHolder().metadataIndex.markBoundsStale(id);
  }

  override isBoundsStale(id: string): boolean {
    return activeHolder().metadataIndex.isBoundsStale(id);
  }

  override clear(): void {
    activeHolder().metadataIndex.clear();
  }

  override getLayerName(metadata: EntityMetadata): string {
    return activeHolder().metadataIndex.getLayerName(metadata);
  }

  override getColorName(metadata: EntityMetadata): string | null {
    return activeHolder().metadataIndex.getColorName(metadata);
  }

  override internedStringCount(): number {
    return activeHolder().metadataIndex.internedStringCount();
  }
}

export const entityMetadataIndex: EntityMetadataIndex =
  new EntityMetadataIndexFacade();

/**
 * Returns the current `CompactStore` for the active document. Always
 * returns the latest reference — `clearCompactCache` swaps the underlying
 * object, so callers should not cache the result across reset boundaries.
 */
export const getCompactCache = (): CompactStore => activeHolder().compactStore;

/**
 * post-V9 Section 0 Iter 4 — populate compact cache from a list of
 * entities (huge-import path). Mutates the active doc's store in place so
 * existing references stay valid.
 *
 * Returns the count of entities successfully appended (skipped entries
 * with unknown `type` field do not count).
 *
 * post-V9 Section 0 Iter 5 — also populates the O(1) id→location index
 * the renderer cache-direct path uses.
 */
export const populateCompactCache = (
  entities: ReadonlyArray<DrawingEntity>,
): number => {
  const holder = activeHolder();
  const store = holder.compactStore;
  const idIndex = holder.idIndex;
  let appended = 0;
  for (const entity of entities) {
    if (entity == null || typeof entity !== "object") continue;
    if (typeof (entity as { type?: unknown }).type !== "string") continue;
    const kind = compactKindForEntity(entity);
    const index = appendCompactEntity(store, entity);
    appended += 1;
    if (kind !== undefined && index >= 0) {
      idIndex.set(entity.id, { storeKind: kind, index });
    }
  }
  // Phase 4 Wave 1 codex P2 + Wave 2 (F2 follow-up) — mark holder as
  // having gone through the cache path. Bulk-populate paths assert the
  // cache is a complete view of the caller-known entity set; the read-
  // through wrapper uses this sticky bit (not idIndex.size) so a fully-
  // drained space still surfaces `[]`. Per-entity helpers
  // (`appendToCompactCache`, `setInCompactCache`) also flip the flag
  // because once any entity is in the cache, the wrapper must trust it.
  // The flag clears only on `clearCompactCache` / `clearMutationStore`.
  holder.cachePopulatedOnce = true;
  // Phase 4 Wave 2 (П35Р follow-up) — bump the cache version so the
  // read-through wrapper's per-space memo invalidates.
  holder.cacheVersion += 1;
  recordCacheVersionBump();
  return appended;
};

/**
 * Phase 1 (P35R) — async chunked variant of `populateCompactCache`.
 * Same semantics, but yields to the event loop every `chunkSize`
 * entities so the main thread can serve input, animation frames, and
 * Playwright probes during a multi-hundred-thousand-entity import.
 *
 * On 685k-entity П35Р the synchronous version blocks the main thread
 * ~3-5s contiguous — long enough for `page.evaluate` probes to time
 * out and for the rendered scene to skip enough frames that the user
 * sees a frozen tab. Chunked yields keep per-tick budget under ~30ms.
 *
 * Final sticky-bit + cache-version flips happen once at the end, same
 * as the synchronous path.
 */
export const populateCompactCacheAsync = async (
  entities: ReadonlyArray<DrawingEntity>,
  chunkSize = 2000,
): Promise<number> => {
  const holder = activeHolder();
  const store = holder.compactStore;
  const idIndex = holder.idIndex;
  let appended = 0;
  for (let i = 0; i < entities.length; i += chunkSize) {
    const end = Math.min(i + chunkSize, entities.length);
    const chunkT0 = perfNow();
    for (let j = i; j < end; j++) {
      const entity = entities[j];
      if (entity == null || typeof entity !== "object") continue;
      if (typeof (entity as { type?: unknown }).type !== "string") continue;
      const kind = compactKindForEntity(entity);
      const index = appendCompactEntity(store, entity);
      appended += 1;
      if (kind !== undefined && index >= 0) {
        idIndex.set(entity.id, { storeKind: kind, index });
      }
    }
    // Phase 1.b (П35Р) — record the per-chunk cost as a synthetic
    // per-entity append so `__import_metrics.appendCount` reflects the
    // populate path. Without this the metric reads 0 even when 685k
    // entities flow through this function.
    //
    // Codex round-5 #13 — collapse the per-entity loop into one
    // `recordAppendBatch` call (same rationale as the batched path
    // below).
    recordAppendBatch(end - i, perfNow() - chunkT0);
    if (end < entities.length) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
  holder.cachePopulatedOnce = true;
  holder.cacheVersion += 1;
  recordCacheVersionBump();
  return appended;
};

/**
 * Phase 1 (P35R) — async variant of `populateCompactCacheFromDocument`.
 * Walks every space and forwards each `space.entities` to the chunked
 * populator. Returns total appended across all spaces.
 */
export const populateCompactCacheFromDocumentAsync = async (
  document: DrawingDocument,
  chunkSize = 2000,
): Promise<number> => {
  let appended = 0;
  for (const space of document.spaces) {
    appended += await populateCompactCacheAsync(space.entities, chunkSize);
  }
  return appended;
};

/**
 * post-V9 Section 0 Iter 4 — replace the active doc's compact cache with
 * a fresh empty store. Cheaper and simpler than mutating individual
 * sub-stores (15 typed-array buffers + maps); the old object is
 * GC-eligible immediately if no caller cached a stale reference.
 *
 * Use `getCompactCache()` after calling this to obtain the new singleton.
 *
 * post-V9 Section 0 Iter 5 — also clears the id→location index.
 *
 * Phase 4 Wave 2 (codex F2 follow-up) — clears the
 * `cachePopulatedOnce` flag so the read-through wrapper falls back
 * to `space.entities` until the next eager populate completes.
 */
export const clearCompactCache = (): void => {
  const holder = activeHolder();
  holder.compactStore = createCompactStore();
  holder.idIndex = new Map();
  // Phase 4 Wave 1 codex P2 — full reset includes the "ever populated"
  // sticky bit; otherwise a freshly-cleared holder would still claim
  // cache authority and suppress the inline-`space.entities` fallback.
  holder.cachePopulatedOnce = false;
  // PR1b Codex P2 FIX-3/FIX-4 — clearing the cache invalidates the
  // streaming-provenance markers (the discarded entities are gone); a
  // subsequent re-import re-records them per chunk.
  holder.streamedSpaceIds = new Set();
  // PR2 (plan §13) — the dependency index is built from the cache + document;
  // wiping the cache invalidates it. A subsequent import rebuilds it via
  // `populateDependencyIndexFromDocument`.
  holder.dependencyIndex.clear();
  // Phase 4 Wave 2 (П35Р follow-up) — bump the cache version so the
  // read-through wrapper's per-space memo invalidates.
  holder.cacheVersion += 1;
  recordCacheVersionBump();
};

/**
 * Phase 4 Wave 2 (codex F2 follow-up) — populate the active doc's
 * compact cache from every entity in `document`. Idempotent against
 * repeated calls only after `clearCompactCache()` (re-running on an
 * already-populated holder would duplicate entries — callers in the
 * import pipeline always clear before re-populating, mirroring the
 * file-import path).
 *
 * Used by `editorStore.importDxfText` and the `importDxfTextAsync`
 * non-worker fallback — both paths previously skipped the populate
 * step, leaving the cache partial. `wrapSpaceAsReadThrough` would
 * then start returning only the entities a later mutation touched,
 * dropping every untouched entity from the reactive document. With
 * this helper called eagerly, the read-through wrapper sees the
 * sticky `cachePopulatedOnce === true` and can trust the snapshot
 * as authoritative.
 *
 * Returns the count of entities successfully appended across all
 * spaces (skipped unknown-type entries are not counted, matching
 * `populateCompactCache`).
 */
export const populateCompactCacheFromDocument = (
  document: DrawingDocument,
): number => {
  let appended = 0;
  for (const space of document.spaces) {
    appended += populateCompactCache(space.entities);
  }
  return appended;
};

/**
 * PR2 (plan §13) — (re)build the active doc's dependency index from a whole
 * document. Call this AFTER `populateCompactCacheFromDocument*` /
 * `markCachePopulated` on the whole-document import path: it reads each space's
 * entities from the document inline array (bulk/legacy import) OR rematerialises
 * them from the compact store (streamed spaces, where `space.entities[]` was
 * discarded), and merges the block-definition edges (always retained on
 * `document.blocks`). INDEX-OVER-EXISTING — no `DrawingEntity` is allocated
 * beyond the snapshot walk the streamed branch already performs.
 *
 * Idempotent: `bulkLoad` clears the index before rebuilding, so re-running on an
 * already-populated holder is safe (mirrors `clearCompactCache` +
 * `populateCompactCacheFromDocument`).
 */
export const populateDependencyIndexFromDocument = (
  document: DrawingDocument,
): void => {
  rebuildHolderDependencyIndex(activeHolder(), document);
};

/**
 * PR2 Codex P2/P3 (#217) — re-sync the active doc's block-DEFINITION dependency
 * containers after a block-def change (BEDIT/BSAVE/REFEDIT, or an ordinary edit
 * during a block-edit session). Precise prev→next diff via `applyBatch`; only
 * changed blocks are walked, never the spaces.
 */
export const refreshBlockDefDependencyIndexContainers = (
  prevDocument: DrawingDocument,
  nextDocument: DrawingDocument,
): void => {
  refreshBlockDefDependencyContainers(activeHolder(), prevDocument, nextDocument);
};

/**
 * PR2 — the active doc's dependency index. Always the latest reference
 * (`clearMutationStore` / `clearCompactCache` reset the index in place; a
 * `setActiveDocument` flip routes to a different holder). Read-only consumers
 * (object-graph facade, future delete-preflight) call `getDependents` on it.
 */
export const getDependencyIndex = (): DependencyIndex =>
  activeHolder().dependencyIndex;

/**
 * PR2 — apply a `commitMutation` `EntityMutation` to the active doc's
 * dependency index incrementally (no full rebuild). Called by `commitMutation`
 * right after the metadata index `applyBatch`.
 */
export const applyDependencyIndexBatch = (
  mutations: EntityMutation,
): void => {
  activeHolder().dependencyIndex.applyBatch(
    dependencyBatchFromMutation(mutations),
  );
};

/**
 * Phase 4 Wave 2 Task 4 — declare the active doc's compact cache
 * authoritative even though the bulk-populate path
 * (`populateCompactCacheFromDocument`) was not used.
 *
 * Streaming-import callers (worker `parseFile` path) build the cache
 * incrementally via `appendToCompactCache`. The per-entity append
 * helper deliberately does NOT flip the sticky `cachePopulatedOnce`
 * flag (see Phase 4 Wave 2 codex F2 follow-up — partial caches must
 * not shadow inline `space.entities` for legacy mutation-only paths).
 *
 * Once the streaming import completes, the caller invokes this helper
 * to flip the flag manually so the read-through wrapper trusts the
 * snapshot. The reactive `geometryDocument.spaces[].entities` arrays
 * are empty in this case (`discardEntitiesAfterStreaming: true`), so
 * any non-trust would surface `[]` everywhere — visible to the user
 * as a blank drawing.
 *
 * No-op if the holder doesn't exist yet.
 */
export const markCachePopulated = (): void => {
  activeHolder().cachePopulatedOnce = true;
};

/**
 * PR1b Codex P2 FIX-3/FIX-4 — record that `spaceId`'s inline entities were
 * DISCARDED by the streaming import (`discardEntitiesAfterStreaming: true`),
 * making the compact cache the authoritative source for that space. Called
 * per-chunk by the streaming chunk consumer in `importDxfFileAsync` /
 * `importDxfFileAsyncReplacing`. Consulted by `buildExportEntitiesSnapshot`
 * to choose cache (streamed) vs inline (legacy/bulk) per space.
 *
 * Writes to the ACTIVE holder — the streaming path pins the active doc id
 * before any chunk fires (see editorStore `setActiveDocument` before the
 * worker is started). No-op semantics: always creates the active holder.
 */
export const markSpaceStreamed = (spaceId: string): void => {
  activeHolder().streamedSpaceIds.add(spaceId);
};

/**
 * LAYOUT-COMMAND-HANDLERS — drop a space's streaming-discarded marker when
 * the space itself is REMOVED from the document (LAYOUT DELETE). Without
 * this, re-creating a layout under the same id (LAYOUT NEW mints the first
 * free "layout-<N>") would inherit a stale cache-authoritative marker from
 * the deleted predecessor. Called by `commitMutation` when the pre/post
 * documents disagree on the space set. No-op when the id was never marked.
 */
export const unmarkSpaceStreamed = (spaceId: string): void => {
  activeHolder().streamedSpaceIds.delete(spaceId);
};

/**
 * LAYOUT-COMMAND-HANDLERS (Codex P2-5) — purge EVERY cached row of a
 * REMOVED space from the active document's compact store, metadata
 * index and dependency index.
 *
 * Why the per-entity `mutations.removed` path is not enough: for a
 * streamed/cache-backed import (`discardEntitiesAfterStreaming`) the
 * deleted space's inline `space.entities` is EMPTY — its entities live
 * ONLY in the compact store — so `diffDocumentMutations` reports
 * nothing and the dead rows + metadata entries would leak (and
 * resurface through every compact reader: read-through wrapper,
 * export snapshot, renderer cache-direct path).
 *
 * Canonical seam: called by `commitMutation` whenever the pre/post
 * documents disagree on the space set (same choke point as
 * `unmarkSpaceStreamed`). Idempotent against the inline path — ids
 * already covered by `mutations.removed` are tombstoned here first and
 * the later per-mutation loop no-ops on them.
 *
 * Returns the SET of purged entity ids (empty = nothing was cached for
 * the space). Codex P2-3 (r5, PR #311): the ids are returned (not just a
 * count) because for a streamed/cache-backed document these are exactly
 * the ids the shell reducer's preview-based `removedEntityIds` filter
 * cannot see — `commitMutation` forwards them so the dispatch pipeline
 * drops them from `selection.entityIds` / `previousPickset` too (one
 * truth: this purge already walks the full cached id set).
 */
export const purgeSpaceFromCaches = (
  spaceId: string,
): ReadonlySet<string> => {
  const holder = activeHolder();
  const ids = new Set<string>();
  // Compact-store rows (streamed imports keep entities ONLY here).
  forEachLiveEntityInSpace(holder, spaceId, (entity) => {
    ids.add(entity.id);
  });
  // Metadata-index entries — covers ids that never reached the compact
  // store (unsupported "Other"-typed entities still get indexed).
  for (const meta of holder.metadataIndex.filterBySpace(spaceId)) {
    ids.add(meta.id);
  }
  if (ids.size === 0) return ids;
  const removedEntries: Array<{ spaceId: string; entityId: string }> = [];
  for (const id of ids) {
    removeFromCompactCache(id);
    removedEntries.push({ spaceId, entityId: id });
  }
  holder.metadataIndex.applyBatch({ upserts: [], deletes: [...ids] });
  // Keep the dependency index in lockstep — delete by (container, id),
  // exactly how the per-mutation removed path does it.
  holder.dependencyIndex.applyBatch(
    dependencyBatchFromMutation({
      added: [],
      removed: removedEntries,
      updated: [],
      styleTablesTouched: NO_MUTATIONS.styleTablesTouched,
    }),
  );
  return ids;
};

/**
 * PR1b Codex P2 FIX-3/FIX-4 — read the streaming-discarded space-id set for a
 * specific document holder. Returns an empty set when the holder is absent
 * (no streaming ever ran for that doc). Read-only view for
 * `buildExportEntitiesSnapshot`.
 */
export const peekStreamedSpaceIds = (docId: string): ReadonlySet<string> => {
  const holder = documentHolders.get(docId);
  return holder ? holder.streamedSpaceIds : EMPTY_STREAMED_SPACE_IDS;
};

const EMPTY_STREAMED_SPACE_IDS: ReadonlySet<string> = new Set();

/**
 * post-V9 Section 0 Iter 5 — O(1) location lookup via the id index.
 * Used by the renderer cache-direct path
 * (`SceneContentRefs.buildPreviewCallbacks`) to lift visible ids in
 * constant time.
 */
export const locateInCompactCache = (
  entityId: string,
): CompactEntityLocation | undefined => {
  return activeHolder().idIndex.get(entityId);
};

/**
 * post-V9 Section 0 Iter 5 — reconstruct a full `DrawingEntity` (or an
 * `AttdefEntity`, which lives in block-definition children) from the
 * compact cache via the id-index. Returns `undefined` if the id is not
 * in the compact cache.
 *
 * This is the hot lookup the renderer's `buildPreview` callback hits
 * before falling back to `getEntityById` (which now walks the reactive
 * geometryDocument as the small-drawing fallback path — Iter 6.D).
 *
 * **Identity contract:** each call returns a freshly-constructed
 * entity. Callers that need `===` identity (e.g. cache keys) should
 * memoise on `entityId` rather than the returned object.
 *
 * **AttDef contract (Phase 3 followup — codex P2):** `appendToCompactCache`
 * / `setInCompactCache` / `compactKindForEntity` accept AttDef and place
 * its id in the index. Previously this getter explicitly dropped AttDef
 * back to `undefined`, leaving the id resolvable in the index but
 * unreachable through the lookup — breaking the block-definition
 * children flow. Now the AttDef branch is returned as-is. Renderer /
 * picking callers (Scene3DStage `buildPreview`, `editorStore.getEntityById`)
 * narrow to `DrawingEntity` themselves — those paths do not touch
 * block-children spaces, so AttDef never reaches the renderer at runtime
 * even though the type now allows it.
 */
export const getEntityFromCompactCache = (
  entityId: string,
): DrawingEntity | AttdefEntity | undefined => {
  const holder = activeHolder();
  const location = holder.idIndex.get(entityId);
  if (location === undefined) return undefined;
  return entityFromCompactStore(
    holder.compactStore,
    location.storeKind as CompactStoreKind,
    location.index,
  );
};

export const findInCompactCache = (
  spaceId: string,
  entityId: string,
): CompactEntityLocation | undefined => {
  const store = activeHolder().compactStore;
  // LINE first (most common in real-world DXFs).
  for (let i = 0; i < store.lines.count; i++) {
    if (store.lines.ids[i] === entityId && store.lines.spaceIds[i] === spaceId) {
      return { storeKind: "lines", index: i };
    }
  }
  const probes: ReadonlyArray<{
    kind: CompactEntityLocation["storeKind"];
    ids: ReadonlyArray<string>;
    spaceIds: ReadonlyArray<string>;
    count: number;
  }> = [
    { kind: "points", ids: store.points.base.ids, spaceIds: store.points.base.spaceIds, count: store.points.count },
    { kind: "circles", ids: store.circles.base.ids, spaceIds: store.circles.base.spaceIds, count: store.circles.count },
    { kind: "arcs", ids: store.arcs.base.ids, spaceIds: store.arcs.base.spaceIds, count: store.arcs.count },
    { kind: "ellipses", ids: store.ellipses.base.ids, spaceIds: store.ellipses.base.spaceIds, count: store.ellipses.count },
    { kind: "splines", ids: store.splines.base.ids, spaceIds: store.splines.base.spaceIds, count: store.splines.count },
    { kind: "lwpolylines", ids: store.lwpolylines.base.ids, spaceIds: store.lwpolylines.base.spaceIds, count: store.lwpolylines.count },
    { kind: "texts", ids: store.texts.base.ids, spaceIds: store.texts.base.spaceIds, count: store.texts.count },
    { kind: "mtexts", ids: store.mtexts.base.ids, spaceIds: store.mtexts.base.spaceIds, count: store.mtexts.count },
    { kind: "dimensions", ids: store.dimensions.base.ids, spaceIds: store.dimensions.base.spaceIds, count: store.dimensions.count },
    { kind: "multileaders", ids: store.multileaders.base.ids, spaceIds: store.multileaders.base.spaceIds, count: store.multileaders.count },
    { kind: "hatches", ids: store.hatches.base.ids, spaceIds: store.hatches.base.spaceIds, count: store.hatches.count },
    { kind: "blockInserts", ids: store.blockInserts.base.ids, spaceIds: store.blockInserts.base.spaceIds, count: store.blockInserts.count },
    { kind: "arrayRects", ids: store.arrayRects.base.ids, spaceIds: store.arrayRects.base.spaceIds, count: store.arrayRects.count },
    { kind: "viewports", ids: store.viewports.base.ids, spaceIds: store.viewports.base.spaceIds, count: store.viewports.count },
    { kind: "revisionClouds", ids: store.revisionClouds.base.ids, spaceIds: store.revisionClouds.base.spaceIds, count: store.revisionClouds.count },
    { kind: "wipeouts", ids: store.wipeouts.base.ids, spaceIds: store.wipeouts.base.spaceIds, count: store.wipeouts.count },
    { kind: "attdefs", ids: store.attdefs.base.ids, spaceIds: store.attdefs.base.spaceIds, count: store.attdefs.count },
    { kind: "polylines", ids: store.polylines.base.ids, spaceIds: store.polylines.base.spaceIds, count: store.polylines.count },
    { kind: "tables", ids: store.tables.base.ids, spaceIds: store.tables.base.spaceIds, count: store.tables.count },
    { kind: "oleFrames", ids: store.oleFrames.base.ids, spaceIds: store.oleFrames.base.spaceIds, count: store.oleFrames.count },
  ];
  for (const p of probes) {
    for (let i = 0; i < p.count; i++) {
      if (p.ids[i] === entityId && p.spaceIds[i] === spaceId) {
        return { storeKind: p.kind, index: i };
      }
    }
  }
  return undefined;
};

/**
 * post-V9 Section 0 Iter 6.C — if the fraction of tombstoned slots in the
 * compact cache exceeds `threshold`, rebuild it with live entries only and
 * remap the id-index. No-op if below threshold.
 *
 * Default threshold 0.25 trades occasional rebuild cost (O(live entities)
 * append) against bounded wasted memory (≤ 25% of column buffers and
 * tombstone bytes are dead at any time).
 *
 * Will be called by mutation-write helpers (Commit 2) after `markTombstone`.
 * Returns `true` if compaction ran.
 */
export const maybeCompactCompactCache = (
  threshold = 0.25,
  holderArg?: DocumentMemoryHolder,
): boolean => {
  const holder = holderArg ?? activeHolder();
  const store = holder.compactStore;
  const fraction = tombstoneFraction(store);
  if (fraction < threshold) return false;
  const { store: newStore, idIndex: newIdIndex } = compactCompactStore(
    store,
    holder.idIndex,
  );
  holder.compactStore = newStore;
  holder.idIndex = newIdIndex;
  return true;
};

/**
 * post-V9 Section 0 Iter 6.C — map a non-`"lines"` store kind to its
 * `BaseColumns` block. Internal helper used by the write-side API to
 * route tombstone-marks through the shared `markTombstone` path.
 *
 * Returns `undefined` for `"lines"` (callers must use the
 * line-specific `markLineTombstone` path) and as an exhaustiveness
 * fall-through for any unmatched future kind.
 */
function baseOfStore(
  store: CompactStore,
  kind: CompactEntityLocation["storeKind"],
): BaseColumns | undefined {
  switch (kind) {
    case "lines":
      return undefined;
    case "points":
      return store.points.base;
    case "circles":
      return store.circles.base;
    case "arcs":
      return store.arcs.base;
    case "ellipses":
      return store.ellipses.base;
    case "splines":
      return store.splines.base;
    case "lwpolylines":
      return store.lwpolylines.base;
    case "texts":
      return store.texts.base;
    case "mtexts":
      return store.mtexts.base;
    case "dimensions":
      return store.dimensions.base;
    case "multileaders":
      return store.multileaders.base;
    case "hatches":
      return store.hatches.base;
    case "blockInserts":
      return store.blockInserts.base;
    case "arrayRects":
      return store.arrayRects.base;
    case "viewports":
      return store.viewports.base;
    case "revisionClouds":
      return store.revisionClouds.base;
    case "wipeouts":
      return store.wipeouts.base;
    case "attdefs":
      return store.attdefs.base;
    case "polylines":
      return store.polylines.base;
    case "tables":
      return store.tables.base;
    case "oleFrames":
      return store.oleFrames.base;
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      return undefined;
    }
  }
}

/**
 * post-V9 Section 0 Iter 6.C — append a new entity to the compact cache
 * and register it in the id-index. Used by `commitMutation` (Commit 3) for
 * each `mutations.added` entry.
 *
 * Returns the slot index in the per-type store, or `-1` if the entity's
 * `type` is not supported by any compact store (e.g., "Other"). The id
 * index is updated only on success (>= 0); failures leave the index
 * untouched so callers can detect them.
 *
 * Idempotency: re-appending an already-indexed id is allowed but
 * shadows the previous slot (the old slot remains live in the column
 * store but is unreachable via the index — it becomes effective garbage
 * until the next compaction). Callers should only call this for entities
 * known to be new.
 */
export const appendToCompactCache = (
  entity: DrawingEntity | AttdefEntity,
): number => appendToCompactCacheInternal(entity, /* silent */ false);

/**
 * Phase 4 Wave 2 (П35Р follow-up) — silent append used by the
 * streaming-import path. Skips the per-entity `entityChanged` broadcast
 * because firing 685k events into the BatchedLayer / spatialIndex
 * subscribers (`addEntity` × N) would re-introduce the full per-entity
 * mesh + R-tree work the streaming-import design is meant to defer to a
 * single post-import bulkLoad.
 *
 * Caller MUST kick a one-shot bulkLoad after the streaming finishes so
 * the renderer + spatial index pick up the appended entities.
 */
export const appendToCompactCacheSilent = (
  entity: DrawingEntity | AttdefEntity,
): number => appendToCompactCacheInternal(entity, /* silent */ true);

/**
 * Phase 4 Wave 2 (П35Р follow-up — streaming wire-up) — batched silent
 * append. Wraps {@link appendToCompactCacheSilent} but resolves the
 * active holder + bumps `cacheVersion` ONCE per batch instead of once
 * per entity. On the 685k-entity П35Р this cuts ~685k holder lookups
 * + ~685k version-bump+metric-record calls down to ~137 (one per
 * STREAM_CHUNK_SIZE=5000 chunk).
 *
 * Skips the per-entity `entityChanged` broadcast (same as the
 * single-entity silent variant) — callers MUST kick a one-shot bulkLoad
 * after the streaming import finishes so the renderer + spatial index
 * pick up the appended entities.
 *
 * Returns the number of entities successfully appended (entries with
 * unknown `type` or non-object payloads are skipped silently, matching
 * the legacy {@link populateCompactCache} contract).
 *
 * No-op for empty arrays.
 */
export const appendBatchToCompactCacheSilent = (
  entities: ReadonlyArray<DrawingEntity | AttdefEntity>,
): number => {
  if (entities.length === 0) return 0;
  const t0 = perfNow();
  const holder = activeHolder();
  const store = holder.compactStore;
  const idIndex = holder.idIndex;
  let appended = 0;
  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i];
    if (entity == null || typeof entity !== "object") continue;
    if (typeof (entity as { type?: unknown }).type !== "string") continue;
    const kind = compactKindForEntity(entity);
    const index = appendCompactEntity(store, entity);
    if (index < 0) continue;
    if (kind !== undefined) {
      idIndex.set(entity.id, { storeKind: kind, index });
    }
    appended += 1;
  }
  // One version bump per batch, not per entity. Same rationale as the
  // sticky-`cachePopulatedOnce` flag below: a partial cache must NOT
  // claim authority (`appendBatchToCompactCacheSilent` is the streaming-
  // import path which calls `markCachePopulated` explicitly once the
  // full document has been drained).
  holder.cacheVersion += 1;
  recordCacheVersionBump();
  // Codex round-5 #13 — collapse per-entity `recordAppend` loop into a
  // single `recordAppendBatch` call. The previous loop did `appended`
  // virtual calls (hundreds of thousands on a 685k import) even when
  // metrics were disabled (the `if (!enabled) return` guard inside
  // `recordAppend` does not amortize the call-site branch + function
  // dispatch overhead JIT'd into the loop). `recordAppendBatch` is
  // itself a no-op when disabled, so the entire metric path costs one
  // function call per batch instead of N.
  const totalMs = appended > 0 ? perfNow() - t0 : 0;
  recordAppendBatch(appended, totalMs);
  return appended;
};

function appendToCompactCacheInternal(
  entity: DrawingEntity | AttdefEntity,
  silent: boolean,
): number {
  const t0 = perfNow();
  const holder = activeHolder();
  const store = holder.compactStore;
  const idIndex = holder.idIndex;
  if (entity == null || typeof entity !== "object") return -1;
  if (typeof (entity as { type?: unknown }).type !== "string") return -1;
  const kind = compactKindForEntity(entity);
  const index = appendCompactEntity(store, entity);
  if (index < 0) return -1;
  if (kind !== undefined) {
    idIndex.set(entity.id, { storeKind: kind, index });
  }
  // Phase 4 Wave 2 (codex F2) — per-entity appends DO NOT flip the
  // sticky `cachePopulatedOnce` flag. Only bulk-populate paths
  // (`populateCompactCache` / `populateCompactCacheFromDocument`)
  // declare the cache complete for a doc. A partial cache (mutations
  // only) must NOT shadow inline `space.entities`, otherwise untouched
  // entities silently disappear from the reactive document.
  // Phase 4 Wave 2 (П35Р follow-up) — bump the cache version so the
  // read-through wrapper's per-space memo invalidates on the next
  // getter access.
  holder.cacheVersion += 1;
  recordCacheVersionBump();
  if (!silent) {
    // Phase 2 Wave 2 Task 1 — broadcast the add so subscribers
    // (BatchedLayer.addEntity, spatialIndex.dirtyAdded, ...) can update
    // incrementally instead of waiting for the next full bulkLoad.
    const event: EntityChangedEvent = {
      id: entity.id,
      spaceId: entity.spaceId,
      kind: "added",
    };
    emitEntityChanged(event);
  }
  recordAppend(perfNow() - t0);
  return index;
}

/**
 * post-V9 Section 0 Iter 6.C — tombstone an entity in the compact cache
 * and purge it from the id-index. Used by `commitMutation` (Commit 3) for
 * each `mutations.removed` entry.
 *
 * After tombstoning, calls `maybeCompactCompactCache()` to rebuild the
 * store with live entries only if the tombstone fraction crossed the
 * default 25% threshold. Compaction happens at most once per remove call
 * and is O(live entities) — bounded by the next-state size.
 *
 * Returns `true` if the entity was found and tombstoned, `false` if no
 * entry for `entityId` existed in the index. Idempotent: a second call
 * for the same id returns `false`.
 */
export const removeFromCompactCache = (entityId: string): boolean => {
  const holder = activeHolder();
  const idIndex = holder.idIndex;
  const location = idIndex.get(entityId);
  if (location === undefined) return false;
  const store = holder.compactStore;
  // Phase 2 Wave 2 Task 1 — capture spaceId BEFORE tombstoning so the
  // "removed" event still carries it. Pulled from the typed column
  // store (lines have their own base, the others share BaseColumns).
  let spaceId = "";
  if (location.storeKind === "lines") {
    spaceId = store.lines.spaceIds[location.index] ?? "";
  } else {
    const base = baseOfStore(store, location.storeKind);
    if (base) spaceId = base.spaceIds[location.index] ?? "";
  }
  let didMark = false;
  if (location.storeKind === "lines") {
    didMark = markLineTombstone(store.lines, location.index);
  } else {
    const base = baseOfStore(store, location.storeKind);
    didMark = base !== undefined && markTombstone(base, location.index);
  }
  idIndex.delete(entityId);
  if (didMark) {
    maybeCompactCompactCache();
    // Phase 4 Wave 2 (П35Р follow-up) — bump cache version for memo
    // invalidation in `wrapSpaceAsReadThrough`.
    holder.cacheVersion += 1;
    recordCacheVersionBump();
    // Phase 2 Wave 2 Task 1 — broadcast after compaction so listeners
    // see a coherent post-state when they re-read the store.
    emitEntityChanged({
      id: entityId,
      spaceId,
      kind: "removed",
    });
  }
  return didMark;
};

/**
 * post-V9 Section 0 Iter 6.C — replace an entity in the compact cache.
 * Used by `commitMutation` (Commit 3) for each `mutations.updated` entry.
 *
 * Implementation: tombstone the old slot + append the new entity +
 * update the id-index to point at the new slot. The old slot becomes
 * eligible for reclamation at the next compaction (default threshold:
 * 25% tombstones).
 *
 * **Why tombstone-and-append (not in-place overwrite):**
 *  - Avoids the need for 14 per-type-store `setEntity(table, index, e)`
 *    helpers (would touch every store file).
 *  - Updates that change `entity.type` (rare but legal — type-conversion
 *    commands) work without a different code path.
 *  - Amortized cost is the same: compaction runs O(live entities) every
 *    ~25% mutations, so an N-entity drawing pays O(N log N) total cost
 *    over a full re-mutation cycle.
 *
 * Returns the new slot index in the per-type store, or `-1` if either:
 *  - The previous entity was not in the index (no `entityId` mapping),
 *  - The new entity's type is unsupported by the compact stores.
 *
 * On `-1`, the id-index is left in whatever state existed before the
 * call (no partial update). Callers are expected to combine with
 * `appendToCompactCache` for entities that may not be indexed yet.
 */
export const setInCompactCache = (
  entityId: string,
  nextEntity: DrawingEntity | AttdefEntity,
): number => setInCompactCacheForHolder(activeHolder(), entityId, nextEntity);

/**
 * Holder-targeted core of {@link setInCompactCache}. Extracted so a docId-keyed
 * caller (`updateCachedEntityInPlace`) can write into a SPECIFIC document's cache
 * without first switching the active document — the streaming viewport-clip
 * finalize must patch the imported doc's cache even when it is not (yet) the
 * routing target (Codex #326 P2 editorStore:949).
 */
const setInCompactCacheForHolder = (
  holder: DocumentMemoryHolder,
  entityId: string,
  nextEntity: DrawingEntity | AttdefEntity,
): number => {
  const idIndex = holder.idIndex;
  const oldLocation = idIndex.get(entityId);
  if (oldLocation === undefined) return -1;
  const store = holder.compactStore;

  // Phase 2 Wave 2 Task 1 — reconstruct the prev entity BEFORE
  // tombstoning so we can classify the diff. `entityFromCompactStore`
  // returns a freshly-built object (identity contract); we don't keep
  // a reference past this scope so the GC reclaims it immediately.
  const prevEntity = entityFromCompactStore(
    store,
    oldLocation.storeKind as CompactStoreKind,
    oldLocation.index,
  );

  // Step 1: tombstone the old slot (no idIndex change yet — we replace
  // the mapping below if the append succeeds).
  if (oldLocation.storeKind === "lines") {
    markLineTombstone(store.lines, oldLocation.index);
  } else {
    const base = baseOfStore(store, oldLocation.storeKind);
    if (base !== undefined) markTombstone(base, oldLocation.index);
  }

  // Step 2: append the new entity. If it has a recognised type the
  // store + index are updated atomically.
  const newKind = compactKindForEntity(nextEntity);
  const newIndex = appendCompactEntity(store, nextEntity);
  if (newIndex < 0 || newKind === undefined) {
    // Unknown type — old is now tombstoned with no replacement. Drop the
    // index entry so consumers don't see a stale pointer at an
    // unreachable slot.
    idIndex.delete(entityId);
    maybeCompactCompactCache(0.25, holder);
    // Treat as a `removed` (no surviving entity) — emit accordingly.
    holder.cacheVersion += 1;
    recordCacheVersionBump();
    if (prevEntity) {
      emitEntityChanged({
        id: entityId,
        spaceId: prevEntity.spaceId,
        kind: "removed",
      });
    }
    return -1;
  }

  idIndex.set(nextEntity.id, { storeKind: newKind, index: newIndex });
  holder.cacheVersion += 1;
  recordCacheVersionBump();
  // If `entityId` differs from `nextEntity.id` (legal for type-change
  // mutations that re-mint the id) clean the stale entry.
  const isRename = nextEntity.id !== entityId;
  if (isRename) idIndex.delete(entityId);

  maybeCompactCompactCache(0.25, holder);

  // F10 (codex P2 #11, post-cycle-1) — id-rename path emits a
  // `removed` for the old id followed by `added` for the new id,
  // not a single `updated`. Subscribers that key their bookkeeping
  // by entity id (`attachBulkLoadIncrementalSubscriber`,
  // `attachSpatialIndexDirtyHook`, BatchedLayer slot map, tessellation
  // cache, ...) would otherwise leave stale entries for the OLD id —
  // the spatial index keeps the old bbox alive, BatchedLayer keeps
  // the old slot occupied, and the renderer's hover/selection paths
  // keep matching against an id that no longer exists in the store.
  // The remove-then-add pair lets every subscriber's standard cleanup
  // run for the old id and standard insert run for the new one.
  if (isRename && prevEntity) {
    emitEntityChanged({
      id: entityId,
      spaceId: prevEntity.spaceId,
      kind: "removed",
    });
    emitEntityChanged({
      id: nextEntity.id,
      spaceId: nextEntity.spaceId,
      kind: "added",
    });
    return newIndex;
  }

  // Phase 2 Wave 2 Task 1 — classify against the (prev, next) pair and
  // broadcast. Style-only changes let the renderer skip tessellation;
  // geometry / both bust it. `prevEntity === undefined` means we
  // couldn't reconstruct the old shape (e.g. partial store corruption)
  // — fall back to "both" so the renderer treats it conservatively.
  const changedFields = prevEntity
    ? classifyEntityChange(prevEntity, nextEntity)
    : "both";
  emitEntityChanged({
    id: nextEntity.id,
    spaceId: nextEntity.spaceId,
    kind: "updated",
    changedFields,
  });
  return newIndex;
};

/**
 * Write a finalized entity BACK into a SPECIFIC document's compact cache, keyed
 * by docId (not the active document). Used by the streaming viewport-clip finalize
 * to patch each viewport's resolved `clipPath` / `childEntityIds` into the cache
 * after the main-thread pass mutated the in-memory document IN PLACE — otherwise a
 * later `wrapSpaceAsReadThrough` rematerialization (read-through treats streamed
 * spaces as cache-authoritative) reconstructs the STALE pre-finalize viewport and
 * drops the resolved fields, rendering/exporting the viewport unclipped on large
 * streamed imports (Codex #326 P2 editorStore:949). No-op when the holder or the
 * entity's cache slot is absent (the viewport may have been streamed without an
 * indexed slot — the in-memory document still carries the finalized fields).
 */
export const updateCachedEntityInPlace = (
  docId: string,
  entity: DrawingEntity,
): boolean => {
  const holder = documentHolders.get(docId);
  if (holder === undefined) return false;
  // Only patch when the entity is actually in this holder's cache index; an
  // entity that was withheld from the cache has nothing to overwrite (its
  // finalized fields live only on the in-memory document, which is fine).
  if (!holder.idIndex.has(entity.id)) return false;
  return setInCompactCacheForHolder(holder, entity.id, entity) >= 0;
};

/**
 * Phase 4 Wave 1 Task B/C — drain the active doc's compact cache and
 * return all live (non-tombstoned) entities whose `spaceId` matches.
 *
 * Used by:
 *  - DXF write path — caller takes a snapshot before serialise so the
 *    writer iterates a stable array even if the cache is mutated mid-
 *    export (`packages/drawing-model/src/dxfBridge.export.ts`).
 *  - `wrapSpaceAsReadThrough(...).entities` getter — Task B materializes
 *    the per-space entity array on each access instead of duplicating
 *    storage in the reactive `geometryDocument.spaces[].entities`.
 *
 * **Identity contract:** each call returns freshly-constructed entity
 * objects (the underlying `entityFromCompactStore` re-builds them per
 * call). Callers that need stable `===` identity must memoise on the
 * outer array reference or on the entity id.
 *
 * Cost: O(total cache count) per call — walks every per-type column
 * once, filters by `spaceId`. For the hot writer path one snapshot per
 * serialise is acceptable (the writer was already O(N)).
 *
 * Phase 4 Wave 2 will add per-space buckets to the compact cache to
 * cut this to O(live-in-space); for Wave 1 we keep the simple full walk.
 *
 * @param docId  Document holder to read from. Use the active doc id
 *               (`getActiveDocumentId()`) for "current document" — the
 *               id parameter lets the writer snapshot a specific doc in
 *               multi-doc scenarios without flipping the active pointer.
 * @param spaceId Filter — only entities whose `spaceId === spaceId` are
 *               returned. Unknown spaceId yields an empty array.
 */
export const snapshotSpaceEntities = (
  docId: string,
  spaceId: string,
): DrawingEntity[] => {
  const t0 = perfNow();
  const holder = documentHolders.get(docId);
  if (holder === undefined) return [];
  const out = snapshotSpaceEntitiesFromHolder(holder, spaceId);
  recordSnapshot(perfNow() - t0);
  return out;
};

/**
 * Phase 4 Wave 2 (П35Р follow-up) — streaming walker for the active
 * document. Wrapper around `forEachLiveEntityInSpace` that resolves the
 * holder by docId. Use this anywhere `snapshotSpaceEntities(...)` was
 * called *only* to walk the result — it avoids materialising the full
 * per-space DrawingEntity[] (~150 MB peak on 685k drawings).
 */
export const forEachSpaceEntity = (
  docId: string,
  spaceId: string,
  cb: (entity: DrawingEntity) => void,
): void => {
  const holder = documentHolders.get(docId);
  if (holder === undefined) return;
  forEachLiveEntityInSpace(holder, spaceId, cb);
};

/**
 * PR #398 @codex P2 (T2 round-2) — holder-wide walker for the active /
 * named document. Resolves the holder by docId and invokes `cb` for EVERY
 * live entity across ALL spaces in ONE linear pass over `idIndex` (no
 * spaceId filter). No-op when the holder is absent.
 *
 * Use this where a caller needs the WHOLE document's cached entities in a
 * single scan — the AI before/after snapshot (`collectAiEntitySnapshot`)
 * does, to avoid the O(spaces × N) cost of calling the per-space
 * `snapshotSpaceEntities` reader once per space (each a full `idIndex`
 * walk). Skips ATTDEF (block-children only), matching `forEachSpaceEntity`.
 */
export const forEachCachedEntity = (
  docId: string,
  cb: (entity: DrawingEntity) => void,
): void => {
  const holder = documentHolders.get(docId);
  if (holder === undefined) return;
  forEachLiveEntity(holder, cb);
};

/**
 * Codex P1 follow-up (autocheck finding 2026-05-18) — generator
 * variant for async callers needing chunked yields mid-walk. Returns
 * an empty generator when the holder is absent. See
 * `iterateLiveEntitiesInSpace` for the underlying iteration semantics.
 */
export function* iterateSpaceEntity(
  docId: string,
  spaceId: string,
): Generator<DrawingEntity, void, undefined> {
  const holder = documentHolders.get(docId);
  if (holder === undefined) return;
  yield* iterateLiveEntitiesInSpace(holder, spaceId);
}

export const getMetadataIndexVersion = (): number =>
  activeHolder().metadataIndexVersion;

export const bumpMetadataIndexVersion = (): number => {
  const holder = activeHolder();
  holder.metadataIndexVersion += 1;
  recordMetadataIndexVersionBump();
  return holder.metadataIndexVersion;
};

export const clearMutationStore = (): void => {
  const holder = activeHolder();
  holder.metadataIndex.clear();
  holder.dependencyIndex.clear();
  holder.compactStore = createCompactStore();
  holder.idIndex = new Map();
  holder.metadataIndexVersion += 1;
  recordMetadataIndexVersionBump();
  // Phase 4 Wave 2 (П35Р follow-up) — bump cache version for memo
  // invalidation in `wrapSpaceAsReadThrough`.
  holder.cacheVersion += 1;
  recordCacheVersionBump();
  // Phase 4 Wave 1 codex P2 — full wipe also drops the "ever populated"
  // sticky bit; see `clearCompactCache`.
  holder.cachePopulatedOnce = false;
  // PR1b Codex P2 FIX-3/FIX-4 — drop streaming-provenance markers on full wipe.
  holder.streamedSpaceIds = new Set();
  // Defect fix (command-created-line-provenance) — full wipe also drops
  // the entity-provenance ledger for the active doc; otherwise a stale
  // "authored" entry from before the wipe could outlive the compact
  // cache it describes (test-isolation hazard: `beforeEach(() =>
  // clearMutationStore())` is the standard per-test reset in this
  // module's test suites).
  entityProvenanceByDocument.delete(activeDocumentId);
};

// Phase 2 Wave 2 Task 1 — re-exports so callers can subscribe to entity
// change events through the same module they import the cache helpers
// from.
export {
  emitEntityChanged,
  subscribeEntityChanged,
  clearEntityChangedListeners,
  getEntityChangedListenerCount,
  type EntityChangedEvent,
  type EntityChangedKind,
} from "./entityChangedBus";
export { type EntityChangedFields } from "./entityChangedClassify";
